import { ApplicationConfig, provideZoneChangeDetection, ErrorHandler } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';

import Bugsnag from '@bugsnag/js';
import BugsnagPerformance, { DefaultRoutingProvider } from '@bugsnag/browser-performance';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

import { routes } from './app.routes';
import { AdminGuard } from './shared/guards/admin.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { UserAdapter } from './shared/models/user.model';

import { LoginService } from './shared/services/login.service';
import { UserAdminService } from './shared/services/user-admin.service';

import { environment } from '../environments/environment';
import { NotificationComponent } from './shared/components/ui/notification/notification/notification.component';
import { FundingRequestService } from './shared/services/funding-request.service';
import { FundingRequestAdapter } from './shared/models/funding-request.model';
import { ChildService } from './shared/services/child.service';
import { ChildAdapter } from './shared/models/child.model';
import { NoteService } from './shared/services/note.service';
import { NoteAdapter } from './shared/models/note.model';
import { FeedbackService } from './shared/services/feedback.service';
import { FeedbackAdapter } from './shared/models/feedback.model';

// Initialize BugSnag
Bugsnag.start({
    apiKey: environment.bugSnagApiKey,
    appVersion: environment.version,
    releaseStage: environment.stage,
    enabledReleaseStages: [ 'production', 'staging' ]
});
BugsnagPerformance.start({
    apiKey: environment.bugSnagApiKey,
    appVersion: environment.version,
    releaseStage: environment.stage,
    enabledReleaseStages: [ 'production', 'staging' ],
    routingProvider: new DefaultRoutingProvider((url: URL): string => {
        if (url.pathname.match(/\/funding-requests\/.*/i)) {
            return '/funding-requests/:request-id';
        }
        else if (url.pathname.match(/\/children\/.*/i)) {
            return '/children/:child-id';
        }
        return url.pathname;
    })
});

// Configure Providers
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    provideToastr({
      toastComponent: NotificationComponent,
      positionClass: 'toast-top-center',
      timeOut: 10000,
      closeButton: true,
      tapToDismiss: false,
      toastClass: 'notification ngx-toastr',
      preventDuplicates: true
    }),
    { provide: ErrorHandler, useFactory: ErrorHandlerFactory },
    AdminGuard,
    AuthGuard,
    LoginService,
    UserAdminService,
    UserAdapter,
    ChildService,
    ChildAdapter,
    FundingRequestService,
    FundingRequestAdapter,
    NoteService,
    NoteAdapter,
    FeedbackService,
    FeedbackAdapter
  ]
};

export function ErrorHandlerFactory() {
    return new BugsnagErrorHandler();
}
